import { useMemo, useState } from 'react'
import TabButtons from '../shared/TabButtons'
import RecentTradesTable from './RecentTradesTable'
import {
  SOL_MINT,
  TRADE_VALUE_FILTER_KEY,
  TRADER_ADDRESS_FILTER_KEY,
  USDC_MINT,
} from 'utils/constants'
import HoldersTable from './HoldersTable'
import { useBirdeyeTokenOverview } from 'hooks/useBirdeyeTokenOverview'
import { useViewport } from 'hooks/useViewport'
import { LinkButton } from '../shared/Button'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { SIDE_FILTER_KEY } from '../modals/SideFilterFormModal'
import MobileTradesFilterModal, {
  calcFilterCount,
} from '../modals/MobileTradesFilterModal'
import { useVenueFilter } from '@components/trade/useSettings'
import store from 'stores/store'
import { useTranslation } from 'next-i18next'

export const HIDE_HOLDERS = [SOL_MINT, USDC_MINT]

type TabKey = 'trades' | 'holders'

const TradesHoldersTabs = () => {
  const { t } = useTranslation('token-page')
  const [activeTradesTab, setActiveTradesTab] = useState<TabKey>('trades')
  const [showMobileFilterForm, setShowMobileFilterForm] = useState(false)
  const { isDesktop } = useViewport()
  const baseMint = store((s) => s.market.baseToken.mint)
  const { data: tokenOverview } = useBirdeyeTokenOverview()
  const [addressToFilter] = useLocalStorage(TRADER_ADDRESS_FILTER_KEY)
  const [valueFilter] = useLocalStorage(TRADE_VALUE_FILTER_KEY)
  const [venueFilter] = useVenueFilter()
  const [sideFilter] = useLocalStorage(SIDE_FILTER_KEY)
  const { holder } = tokenOverview ?? {}
  const showHolders = baseMint && !HIDE_HOLDERS.includes(baseMint)

  const filterCount = useMemo(
    () =>
      calcFilterCount(addressToFilter, sideFilter, valueFilter, venueFilter),
    [addressToFilter, sideFilter, valueFilter, venueFilter],
  )

  const handleTabChange = (tab: string) => {
    if (tab === t('trades.trades')) {
      setActiveTradesTab('trades')
    } else if (tab === t('trades.holders')) {
      setActiveTradesTab('holders')
    }
  }

  return (
    <>
      <div
        className={`shared-container h-full ${
          showHolders ? 'lg:pb-6' : 'pt-6 lg:pb-2'
        }`}
      >
        <div
          className={`flex ${
            showHolders ? 'items-end' : 'items-center px-4 pb-2'
          } justify-between`}
        >
          {showHolders ? (
            <TabButtons
              activeValue={t(`trades.${activeTradesTab}`)}
              values={[
                [t('trades.trades'), 0],
                [t('trades.holders'), holder || 0],
              ]}
              onChange={handleTabChange}
            />
          ) : (
            <h2 className="text-base">{t('trades.trades')}</h2>
          )}
          {!isDesktop ? (
            <div
              className={`flex items-center ${
                showHolders ? 'h-12 border-b border-th-bkg-3' : ''
              } bg-th-bkg-1 px-4`}
            >
              <LinkButton
                className="relative text-sm font-normal"
                onClick={() => setShowMobileFilterForm(true)}
              >
                <span className="text-th-fgd-1">{t('trades.filters')}</span>
                {filterCount ? (
                  <div className="absolute -right-2.5 -top-2">
                    <span className="relative flex h-3.5 w-max items-center justify-center rounded-full bg-th-down px-1 text-xxs font-bold text-white">
                      {filterCount}
                    </span>
                  </div>
                ) : null}
              </LinkButton>
            </div>
          ) : null}
        </div>
        <div
          className={`thin-scroll h-[calc(100%-24px)] overflow-auto rounded-t-none`}
        >
          {activeTradesTab === 'trades' ? (
            <RecentTradesTable />
          ) : (
            <HoldersTable setActiveTradesTab={setActiveTradesTab} />
          )}
        </div>
      </div>
      {showMobileFilterForm ? (
        <MobileTradesFilterModal
          isOpen={showMobileFilterForm}
          onClose={() => setShowMobileFilterForm(false)}
        />
      ) : null}
    </>
  )
}

export default TradesHoldersTabs
